import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import {
  Button,
  StandardDialog,
  createCurrencyField,
  createDateField,
  createSelectField,
  createTextField,
} from '@roc/ui';
import {
  LEASE_TYPE_OPTIONS,
  VACANT_OPTIONS,
  SECTION_842_OPTIONS,
} from '../store/leaseFormStore';
import { useStore } from '@roc/client-portal-shared/hooks';
import { DateFormat, formatDate } from '@roc/feature-utils';

export interface EditLeaseModalProps {
  open: boolean;
  onClose: () => void;
  editingLeaseId?: number | null;
}

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: 0,
  },
  closeButton: {
    border: '1px solid #D0D0D0',
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
    marginRight: theme.spacing(1),
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
  saveButton: {
    textTransform: 'none',
    fontWeight: 600,
    width: 120,
  },
}));

export const EditLeaseModal = observer(
  ({ open, onClose, editingLeaseId }: EditLeaseModalProps) => {
    const classes = useStyles();
    const { leaseStore } = useStore();
    const store = leaseStore.leaseFormStore;

    const isVacant = store.form.fields.vacant.value === true;

    const dialogContent = (
      <Grid container spacing={1}>
        <Grid item xs={6}>
          {createSelectField({
            store,
            fieldName: 'vacant',
            testId: 'vacant',
            label: 'Vacant',
            options: VACANT_OPTIONS,
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'unitNumber',
            testId: 'unitNumber',
            label: 'Unit',
          })}
        </Grid>
        <Grid item xs={6}>
          {createTextField({
            store,
            fieldName: 'tenantName',
            testId: 'tenantName',
            label: 'Tenant Name',
            isRequired: !isVacant,
          })}
        </Grid>
        <Grid item xs={6}>
          {createSelectField({
            store,
            fieldName: 'leaseType',
            testId: 'leaseType',
            label: 'Lease Type',
            options: LEASE_TYPE_OPTIONS,
            isRequired: !isVacant,
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'startDate',
            testId: 'startDate',
            label: 'Start Date',
            isRequired: !isVacant,
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={6}>
          {createDateField({
            store,
            fieldName: 'endDate',
            testId: 'endDate',
            label: 'End Date',
            onChange: (name, date) =>
              store.onFieldChange(name, formatDate(date, DateFormat.MMDDYYYY)),
          })}
        </Grid>
        <Grid item xs={6}>
          {createCurrencyField({
            store,
            fieldName: 'monthlyRent',
            testId: 'monthlyRent',
            label: 'Monthly Rent',
            isRequired: !isVacant,
          })}
        </Grid>
        <Grid item xs={6}>
          {createCurrencyField({
            store,
            fieldName: 'marketRent',
            testId: 'marketRent',
            label: 'Market Rent',
          })}
        </Grid>
        <Grid item xs={6}>
          {createSelectField({
            store,
            fieldName: 'sectionEightFourTwo',
            testId: 'sectionEightFourTwo',
            label: 'Section 8/42',
            options: SECTION_842_OPTIONS,
          })}
        </Grid>
      </Grid>
    );

    const handleSave = async () => {
      await leaseStore.saveLease(editingLeaseId);
      onClose();
    };

    const dialogActions = (
      <>
        <Button
          className={classes.closeButton}
          variant="outlined"
          onClick={onClose}
          testId="edit-lease-cancel"
        >
          Cancel
        </Button>
        <Button
          className={classes.saveButton}
          variant="contained"
          color="primary"
          onClick={handleSave}
          testId="edit-lease-save"
        >
          Save
        </Button>
      </>
    );

    return (
      <StandardDialog
        open={open}
        title={editingLeaseId != null ? 'Edit Lease' : 'Add Lease'}
        maxWidth="sm"
        fullWidth
        handleClose={onClose}
        dialogContent={dialogContent}
        dialogActions={dialogActions}
        dialogContentclasses={{ root: classes.dialogContent }}
      />
    );
  }
);
